/**
 * Template Seeding Page
 *
 * Allows platform admins to seed templates from master_db to tenant databases.
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
  Alert,
  CircularProgress,
  Chip,
  RadioGroup,
  Radio,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import {
  ContentCopy as SeedIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Preview as PreviewIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { tenantService, templateService, industryPackService } from "../services";
import {
  TemplateEntityType,
  SeedConflictStrategy,
  type SeedTemplatesResponse,
  type SyncSystemTemplatesResponse,
} from "../services/templateService";
import type { IndustryPackListItem } from "../services/industryPackService";

type SeedSource = "master" | "pack";

type PackContentKey = keyof IndustryPackListItem["contentCounts"];

/**
 * Maps a TemplateEntityType to its corresponding `contentCounts` key on
 * IndustryPackListItem. Entity types not present in this map are NOT carried
 * by the industry-pack interface (e.g. SOURCES, STATUS_FLOWS, TASK_TYPES) and
 * are hidden from the entity-type picker when source=pack.
 */
const PACK_CONTENT_KEY_BY_ENTITY_TYPE: Partial<
  Record<TemplateEntityType, PackContentKey>
> = {
  [TemplateEntityType.CHECKLIST_GROUPS]: "checklistGroups",
  [TemplateEntityType.FOLDER_TEMPLATES]: "folderTemplates",
  [TemplateEntityType.ACTIVITY_TYPES]: "activityTypes",
  [TemplateEntityType.CONSENT_BASES]: "consentBases",
};

export function TemplateSeedingPage() {
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [selectedEnvironment, setSelectedEnvironment] =
    useState<string>("production");
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<
    TemplateEntityType[]
  >([]);
  const [conflictStrategy, setConflictStrategy] = useState<SeedConflictStrategy>(
    SeedConflictStrategy.SKIP
  );
  const [source, setSource] = useState<SeedSource>("master");
  const [selectedPackKey, setSelectedPackKey] = useState<string | null>(null);
  const [seedResult, setSeedResult] = useState<SeedTemplatesResponse | null>(
    null
  );
  const [systemSyncResult, setSystemSyncResult] =
    useState<SyncSystemTemplatesResponse | null>(null);

  // Queries
  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => tenantService.getAll(),
  });

  // Fetch the selected tenant's actual environments so the env selector shows
  // what the tenant really has (e.g. 'staging') instead of a hardcoded
  // Production/Test — the seeding endpoints resolve the tenant DB by env NAME,
  // so a mismatch 404s ("Tenant environment not found").
  const tenantDetailQuery = useQuery({
    queryKey: ["tenant-detail", selectedTenantId],
    queryFn: () => tenantService.getById(selectedTenantId),
    enabled: !!selectedTenantId,
  });

  const environments = (tenantDetailQuery.data?.environments ?? []).filter(
    (e) => e.isActive
  );

  const templatesQuery = useQuery({
    queryKey: ["available-templates"],
    queryFn: () => templateService.getAvailableTemplates(),
  });

  const selectableTypesQuery = useQuery({
    queryKey: ["selectable-types"],
    queryFn: () => templateService.getSelectableTypes(),
  });

  const availablePacksQuery = useQuery({
    queryKey: ["industry-packs"],
    queryFn: () => industryPackService.getAvailablePacks(),
  });

  // Mutation — used for both live seeding and dry-run preview. The mutation
  // function passes through whatever `dryRun` flag the caller put on the
  // request, so the success-snackbar copy needs to disambiguate from the
  // returned response (`data.dryRun`) rather than from a closed-over flag.
  const seedMutation = useMutation({
    mutationFn: templateService.seedTemplates,
    onSuccess: (data) => {
      setSeedResult(data);
      if (data.dryRun) {
        if (data.success) {
          enqueueSnackbar("Forhåndsvisning fullført — ingen endringer er gjort", {
            variant: "info",
          });
        } else {
          enqueueSnackbar("Forhåndsvisning fullført med feil", {
            variant: "warning",
          });
        }
      } else if (data.success) {
        enqueueSnackbar("Templates seeded successfully!", { variant: "success" });
      } else {
        enqueueSnackbar("Some templates failed to seed", { variant: "warning" });
      }
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Seeding failed: ${error.message}`, { variant: "error" });
    },
  });

  // System templates (product templates from CODE) — a separate operation from
  // the master_db seeding above. Upserts the current code set and deactivates
  // removed keys for the selected tenant + environment.
  const systemSyncMutation = useMutation({
    mutationFn: templateService.syncSystemTemplatesToTenant,
    onSuccess: (data) => {
      setSystemSyncResult(data);
      enqueueSnackbar("System-maler synket!", { variant: "success" });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Synk feilet: ${error.message}`, { variant: "error" });
    },
  });

  const handleSyncSystemTemplates = () => {
    if (!selectedTenantId) {
      enqueueSnackbar("Velg en tenant først", { variant: "warning" });
      return;
    }
    setSystemSyncResult(null);
    systemSyncMutation.mutate({
      tenantId: selectedTenantId,
      environment: selectedEnvironment,
    });
  };

  const selectedPack: IndustryPackListItem | undefined = selectedPackKey
    ? availablePacksQuery.data?.find((p) => p.key === selectedPackKey)
    : undefined;

  // Initialize selected entity types when the source / selectable types /
  // selected-pack change. For master source: pre-select all selectable types.
  // For pack source: pre-select only the entity types the chosen pack carries
  // a non-zero count for. While no pack is selected, leave the selection
  // empty so the operator must pick a pack first.
  useEffect(() => {
    const selectableTypes = selectableTypesQuery.data?.types;
    if (!selectableTypes) return;

    if (source === "master") {
      setSelectedEntityTypes(selectableTypes);
      return;
    }

    if (!selectedPack) {
      setSelectedEntityTypes([]);
      return;
    }

    const supported = selectableTypes.filter((type) => {
      const key = PACK_CONTENT_KEY_BY_ENTITY_TYPE[type];
      if (!key) return false;
      return (selectedPack.contentCounts[key] ?? 0) > 0;
    });
    setSelectedEntityTypes(supported);
  }, [source, selectableTypesQuery.data, selectedPack]);

  // Industry packs are additive-only by design (server-enforced via
  // INDUSTRY_PACK_OVERWRITE_FORBIDDEN). Force conflictStrategy back to SKIP
  // when the operator switches source to pack so the disabled Overskriv
  // radio doesn't leave a stale OVERWRITE selection in state.
  useEffect(() => {
    if (source === "pack" && conflictStrategy !== SeedConflictStrategy.SKIP) {
      setConflictStrategy(SeedConflictStrategy.SKIP);
    }
  }, [source, conflictStrategy]);

  // When the selected tenant's environments load, snap the env selection to a
  // valid one (avoids requesting a 'production' env a staging tenant lacks).
  useEffect(() => {
    const active = (tenantDetailQuery.data?.environments ?? []).filter(
      (e) => e.isActive
    );
    if (active.length === 0) return;
    if (!active.some((e) => e.name === selectedEnvironment)) {
      setSelectedEnvironment(active[0].name);
    }
  }, [tenantDetailQuery.data, selectedEnvironment]);

  // Handlers
  const handleEntityTypeToggle = (entityType: TemplateEntityType) => {
    setSelectedEntityTypes((prev) =>
      prev.includes(entityType)
        ? prev.filter((t) => t !== entityType)
        : [...prev, entityType]
    );
  };

  const handleSelectAll = () => {
    const types = selectableTypesQuery.data?.types;
    if (!types) return;

    if (source === "master") {
      setSelectedEntityTypes(types);
      return;
    }

    if (!selectedPack) {
      setSelectedEntityTypes([]);
      return;
    }

    const supported = types.filter((type) => {
      const key = PACK_CONTENT_KEY_BY_ENTITY_TYPE[type];
      if (!key) return false;
      return (selectedPack.contentCounts[key] ?? 0) > 0;
    });
    setSelectedEntityTypes(supported);
  };

  const handleDeselectAll = () => {
    setSelectedEntityTypes([]);
  };

  // Resolve the wire-format `source` value the backend expects: 'master' or
  // 'pack:<key>'. The pack-key version is only valid when a pack has been
  // chosen — callers must check before invoking the seeder.
  const resolveSourceParam = (): string | null => {
    if (source === "master") return "master";
    if (source === "pack" && selectedPackKey) return `pack:${selectedPackKey}`;
    return null;
  };

  // Both Start Seeding and Forhåndsvis go through the same mutation; the only
  // difference is the `dryRun` flag on the request. Validation is shared.
  const submitSeed = (dryRun: boolean) => {
    if (!selectedTenantId) {
      enqueueSnackbar("Please select a tenant", { variant: "warning" });
      return;
    }
    if (selectedEntityTypes.length === 0) {
      enqueueSnackbar("Please select at least one entity type", {
        variant: "warning",
      });
      return;
    }
    const sourceParam = resolveSourceParam();
    if (!sourceParam) {
      enqueueSnackbar("Velg en industri-pakke før du seeder", {
        variant: "warning",
      });
      return;
    }

    setSeedResult(null);
    seedMutation.mutate({
      tenantId: selectedTenantId,
      environment: selectedEnvironment,
      entityTypes: selectedEntityTypes,
      conflictStrategy,
      source: sourceParam,
      dryRun,
    });
  };

  const handleSeed = () => submitSeed(false);
  const handlePreview = () => submitSeed(true);

  // Get tenant info
  const tenants = tenantsQuery.data?.tenants || [];
  const selectedTenant = tenants.find((t) => t.id === selectedTenantId);
  const templates = templatesQuery.data?.templates;
  const selectableTypes = selectableTypesQuery.data?.types || [];
  const availablePacks = availablePacksQuery.data || [];
  const isPackSource = source === "pack";
  const seedDisabled =
    seedMutation.isPending ||
    !selectedTenantId ||
    selectedEntityTypes.length === 0 ||
    (isPackSource && !selectedPackKey);

  // Loading state
  if (tenantsQuery.isLoading || templatesQuery.isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Template Seeding
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Seed templates from the master database to tenant databases. This allows
        you to push standard templates (sources, checklists, activity types,
        etc.) to existing tenants.
      </Typography>

      {/* Master Template Stats */}
      {templates && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Available Templates in Master Database
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {Object.entries(templates)
              .filter(([, data]) => !data.isCore && data.count > 0)
              .map(([type, data]) => (
                <Chip
                  key={type}
                  label={`${data.displayNameNo}: ${data.count}`}
                  color="primary"
                  variant="outlined"
                />
              ))}
          </Stack>
        </Paper>
      )}

      {/* Step 0: Source (Master vs Industry Pack) */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Kilde
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup
            value={source}
            onChange={(e) => setSource(e.target.value as SeedSource)}
          >
            <FormControlLabel
              value="master"
              control={<Radio />}
              label="Master database (standard generiske templates)"
            />
            <FormControlLabel
              value="pack"
              control={<Radio />}
              label="Industri-pakke"
            />
          </RadioGroup>
        </FormControl>
        {isPackSource && (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="pack-select-label">Velg pakke</InputLabel>
            <Select
              labelId="pack-select-label"
              id="pack-select"
              value={selectedPackKey ?? ""}
              onChange={(e) => setSelectedPackKey(e.target.value || null)}
              label="Velg pakke"
              disabled={availablePacksQuery.isLoading}
            >
              {availablePacks.map((pack) => (
                <MenuItem key={pack.key} value={pack.key}>
                  {pack.displayName} (v{pack.version})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isPackSource &&
          !availablePacksQuery.isLoading &&
          availablePacks.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Ingen industri-pakker er registrert.
            </Alert>
          )}
      </Paper>

      {/* Step 1: Select Tenant */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          1. Velg Tenant
        </Typography>

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="tenant-select-label">Tenant</InputLabel>
          <Select
            labelId="tenant-select-label"
            id="tenant-select"
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            label="Tenant"
          >
            {tenants
              .filter((t) => t.slug !== "master") // Exclude master tenant
              .map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.name} ({tenant.slug})
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {selectedTenant && (
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Environment</FormLabel>
              {tenantDetailQuery.isLoading ? (
                <Box sx={{ mt: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : environments.length === 0 ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Denne tenanten har ingen aktive miljøer.
                </Alert>
              ) : (
                <RadioGroup
                  row
                  value={selectedEnvironment}
                  onChange={(e) => setSelectedEnvironment(e.target.value)}
                >
                  {environments.map((env) => (
                    <FormControlLabel
                      key={env.id}
                      value={env.name}
                      control={<Radio />}
                      label={
                        env.name.charAt(0).toUpperCase() + env.name.slice(1)
                      }
                    />
                  ))}
                </RadioGroup>
              )}
            </FormControl>
          </Box>
        )}
      </Paper>

      {/* System templates (product, from code) — SEPARATE from master seeding.
          Master "Overskriv" below copies CUSTOM templates and cannot touch
          system templates (isSystemTemplate=true is filtered out server-side). */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderLeft: "4px solid",
          borderColor: "secondary.main",
        }}
      >
        <Typography variant="h6" gutterBottom>
          System-maler (produkt, fra kode)
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Produkt-SMS/e-postmaler som leveres i koden (f.eks. «Booket
          kundemøte», montør-varsler). Dette er <strong>ikke</strong> det samme
          som «Overskriv» lenger ned — den kopierer <em>custom</em>-maler fra
          master. Synk oppdaterer system-malene til gjeldende kode-sett og
          deaktiverer fjernede. Kjør etter deploy for eksisterende tenants (nye
          tenants får dem automatisk ved opprettelse).
        </Alert>
        <Button
          variant="contained"
          color="secondary"
          startIcon={
            systemSyncMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SeedIcon />
            )
          }
          onClick={handleSyncSystemTemplates}
          disabled={systemSyncMutation.isPending || !selectedTenantId}
        >
          {systemSyncMutation.isPending
            ? "Synker..."
            : "Synk system-maler til denne tenanten"}
        </Button>
        {selectedTenant && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Synker system-maler til <strong>{selectedTenant.name}</strong> (
            {selectedEnvironment})
          </Typography>
        )}
        {systemSyncResult && (
          <Alert severity="success" sx={{ mt: 2 }}>
            SMS: {systemSyncResult.smsUpserted} oppdatert,{" "}
            {systemSyncResult.smsDeactivated} deaktivert · E-post:{" "}
            {systemSyncResult.emailUpserted} oppdatert,{" "}
            {systemSyncResult.emailDeactivated} deaktivert
          </Alert>
        )}
      </Paper>

      {/* Step 2: Select Entity Types */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">2. Velg Template-typer</Typography>
          <Box>
            <Button size="small" onClick={handleSelectAll} sx={{ mr: 1 }}>
              Velg alle
            </Button>
            <Button size="small" onClick={handleDeselectAll}>
              Fjern alle
            </Button>
          </Box>
        </Box>

        <FormGroup>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {selectableTypes
              .filter((type) => {
                // Pack source: hide entity types the pack interface does not
                // claim to support. Master source: show every selectable type.
                if (!isPackSource) return true;
                return type in PACK_CONTENT_KEY_BY_ENTITY_TYPE;
              })
              .map((type) => {
                const templateData = templates?.[type];
                const packKey = PACK_CONTENT_KEY_BY_ENTITY_TYPE[type];
                const packCount =
                  isPackSource && selectedPack && packKey
                    ? selectedPack.contentCounts[packKey] ?? 0
                    : null;
                const isPackEmpty = isPackSource && packCount === 0;
                const checkboxDisabled = isPackEmpty;
                const checked =
                  !checkboxDisabled && selectedEntityTypes.includes(type);

                let countCaption: string | null = null;
                if (isPackSource) {
                  if (packCount === null) {
                    // Awaiting pack selection — show no count yet.
                    countCaption = null;
                  } else if (packCount === 0) {
                    countCaption = "tom for denne pakken";
                  } else {
                    countCaption = `${packCount} i pakken`;
                  }
                } else if (templateData) {
                  countCaption = `${templateData.count} tilgjengelig`;
                }

                return (
                  <FormControlLabel
                    key={type}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={() => handleEntityTypeToggle(type)}
                        disabled={checkboxDisabled}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">
                          {templateData?.displayNameNo || type}
                        </Typography>
                        {countCaption && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {countCaption}
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{ minWidth: 180, m: 0.5 }}
                  />
                );
              })}
          </Stack>
        </FormGroup>
      </Paper>

      {/* Step 3: Conflict Strategy */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          3. Konflikthåndtering
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          Velg hvordan eksisterende data i target-tenant skal håndteres.
        </Alert>

        <FormControl component="fieldset">
          <RadioGroup
            value={conflictStrategy}
            onChange={(e) =>
              setConflictStrategy(e.target.value as SeedConflictStrategy)
            }
          >
            <FormControlLabel
              value={SeedConflictStrategy.SKIP}
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">Hopp over (anbefalt)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Behold eksisterende data. Kun nye elementer blir opprettet.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value={SeedConflictStrategy.OVERWRITE}
              control={<Radio disabled={isPackSource} />}
              disabled={isPackSource}
              label={
                <Box>
                  <Typography variant="body1">Overskriv</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Erstatt eksisterende data med master-templates. Kan ikke
                    angres!
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {isPackSource && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Industri-pakker kan ikke overskrive eksisterende tenant-data
            (sikkerhetsregel — pakker er kun additiv installasjon).
          </Alert>
        )}
      </Paper>

      {/* Action Buttons */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            size="large"
            startIcon={<PreviewIcon />}
            onClick={handlePreview}
            disabled={seedDisabled}
          >
            Forhåndsvis (dry-run)
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={
              seedMutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SeedIcon />
              )
            }
            onClick={handleSeed}
            disabled={seedDisabled}
          >
            {seedMutation.isPending ? "Seeder..." : "Start Seeding"}
          </Button>
        </Stack>

        {selectedTenant && selectedEntityTypes.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Vil seede {selectedEntityTypes.length} template-typer til{" "}
            <strong>{selectedTenant.name}</strong> ({selectedEnvironment})
          </Typography>
        )}
      </Paper>

      {/* Results */}
      {seedResult && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {seedResult.dryRun
              ? "Forhåndsvisning (ingen endringer er gjort)"
              : "Resultat"}
          </Typography>

          {seedResult.dryRun && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Forhåndsvisning — ingen endringer er gjort i tenant-databasen.
              Klikk "Start Seeding" for å committe.
            </Alert>
          )}

          <Alert
            severity={seedResult.success ? "success" : "warning"}
            sx={{ mb: 2 }}
          >
            {seedResult.success
              ? seedResult.dryRun
                ? "Forhåndsvisning fullført uten feil!"
                : "Seeding fullført uten feil!"
              : seedResult.dryRun
                ? "Forhåndsvisning fullført med noen feil. Se detaljer nedenfor."
                : "Seeding fullført med noen feil. Se detaljer nedenfor."}
          </Alert>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">
                    {seedResult.dryRun ? "Vil opprette" : "Opprettet"}
                  </TableCell>
                  <TableCell align="right">
                    {seedResult.dryRun ? "Vil oppdatere" : "Oppdatert"}
                  </TableCell>
                  <TableCell align="right">
                    {seedResult.dryRun ? "Vil hoppe over" : "Hoppet over"}
                  </TableCell>
                  <TableCell>Feil</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {seedResult.results.map((result) => {
                  const createdCount = seedResult.dryRun
                    ? result.wouldCreate?.length ?? 0
                    : result.created;
                  const updatedCount = seedResult.dryRun
                    ? result.wouldUpdate?.length ?? 0
                    : result.updated;
                  const skippedCount = seedResult.dryRun
                    ? result.wouldSkip?.length ?? 0
                    : result.skipped;
                  return (
                    <TableRow key={result.entityType}>
                      <TableCell>
                        {templates?.[result.entityType]?.displayNameNo ||
                          result.entityType}
                      </TableCell>
                      <TableCell>
                        {result.success ? (
                          <SuccessIcon color="success" fontSize="small" />
                        ) : (
                          <ErrorIcon color="error" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">{createdCount}</TableCell>
                      <TableCell align="right">{updatedCount}</TableCell>
                      <TableCell align="right">{skippedCount}</TableCell>
                      <TableCell>
                        {result.error && (
                          <Typography variant="caption" color="error">
                            {result.error}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2 }}>
            {seedResult.dryRun ? (
              <Typography variant="body2" color="text.secondary">
                Vil opprette:{" "}
                {seedResult.results.reduce(
                  (sum, r) => sum + (r.wouldCreate?.length ?? 0),
                  0
                )}{" "}
                | Vil oppdatere:{" "}
                {seedResult.results.reduce(
                  (sum, r) => sum + (r.wouldUpdate?.length ?? 0),
                  0
                )}{" "}
                | Vil hoppe over:{" "}
                {seedResult.results.reduce(
                  (sum, r) => sum + (r.wouldSkip?.length ?? 0),
                  0
                )}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Totalt opprettet:{" "}
                {seedResult.results.reduce((sum, r) => sum + r.created, 0)} |
                Oppdatert:{" "}
                {seedResult.results.reduce((sum, r) => sum + r.updated, 0)} |
                Hoppet over:{" "}
                {seedResult.results.reduce((sum, r) => sum + r.skipped, 0)}
              </Typography>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
